import React from 'react';
import {
  DollarSign,
  Cpu,
  TrendingUp,
  Calendar,
  Zap,
  Clock,
  AlertTriangle,
} from 'lucide-react';
import type { AICostDashboardData } from '../types';

export interface AICostDashboardProps {
  data: AICostDashboardData;
  className?: string;
}

const formatCurrency = (value: number): string => {
  if (value >= 1) return `$${value.toFixed(2)}`;
  if (value >= 0.01) return `$${value.toFixed(3)}`;
  if (value >= 0.0001) return `$${value.toFixed(4)}`;
  return '$0.00';
};

const formatNumber = (value: number): string => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toString();
};

const tierColors: Record<string, string> = {
  fast: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  standard: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  premium: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
};

export const AICostDashboard: React.FC<AICostDashboardProps> = ({
  data,
  className = '',
}) => {
  const { modelConfig, costSummary, dailyCosts, byModel, byTier, budget } = data;

  // Calculate max cost for bar chart scaling
  const maxDailyCost = dailyCosts.length > 0
    ? Math.max(...dailyCosts.map((d) => d.totalCost))
    : 0;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Budget Alert */}
      {budget.percentUsed >= 80 && (
        <div
          className={`rounded-lg p-4 ${
            budget.percentUsed >= 90
              ? 'bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800'
              : 'bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800'
          }`}
        >
          <div className="flex items-center gap-3">
            <AlertTriangle
              className={`h-5 w-5 ${
                budget.percentUsed >= 90 ? 'text-red-500' : 'text-yellow-500'
              }`}
            />
            <div>
              <p
                className={`font-medium ${
                  budget.percentUsed >= 90
                    ? 'text-red-800 dark:text-red-300'
                    : 'text-yellow-800 dark:text-yellow-300'
                }`}
              >
                {budget.percentUsed >= 90 ? 'Budget Critical' : 'Budget Warning'}
              </p>
              <p
                className={`text-sm ${
                  budget.percentUsed >= 90
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-yellow-600 dark:text-yellow-400'
                }`}
              >
                {budget.percentUsed.toFixed(0)}% of daily budget used ({formatCurrency(budget.used)} / {formatCurrency(budget.daily)}).
                {budget.percentUsed >= 90
                  ? ' Processing may be limited.'
                  : ' Consider reviewing usage.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Model Configuration */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
          <Cpu className="h-4 w-4" />
          Active Model Configuration
        </h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {['fast', 'standard', 'premium'].map((tier) => {
            const config = modelConfig[tier];
            return (
              <div
                key={tier}
                className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4"
              >
                <div className="flex items-center justify-between mb-2">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      tierColors[tier] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                    }`}
                  >
                    {tier}
                  </span>
                </div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate" title={config?.model || 'Not configured'}>
                  {config?.model || 'Not configured'}
                </p>
                {config?.updatedAt && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Updated {new Date(config.updatedAt).toLocaleDateString()}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Cost Summary Cards */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
          <DollarSign className="h-4 w-4" />
          Cost Summary
        </h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="h-4 w-4 text-blue-500" />
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Today</p>
            </div>
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {formatCurrency(costSummary.daily.totalCost)}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {costSummary.daily.reviewCount} review{costSummary.daily.reviewCount !== 1 ? 's' : ''}
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="h-4 w-4 text-green-500" />
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">This Week</p>
            </div>
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {formatCurrency(costSummary.weekly.totalCost)}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {costSummary.weekly.reviewCount} review{costSummary.weekly.reviewCount !== 1 ? 's' : ''}
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="h-4 w-4 text-purple-500" />
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Last 30 Days</p>
            </div>
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {formatCurrency(costSummary.monthly.totalCost)}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {costSummary.monthly.reviewCount} review{costSummary.monthly.reviewCount !== 1 ? 's' : ''}
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="h-4 w-4 text-orange-500" />
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Avg / Review</p>
            </div>
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {formatCurrency(costSummary.avgCostPerReview)}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {costSummary.avgProcessingTimeMs > 0
                ? `${(costSummary.avgProcessingTimeMs / 1000).toFixed(1)}s avg time`
                : 'No data'}
            </p>
          </div>
        </div>
      </div>

      {/* Budget Progress */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
          <Zap className="h-4 w-4" />
          Daily Budget
        </h3>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {formatCurrency(budget.used)} used of {formatCurrency(budget.daily)}
            </span>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {budget.percentUsed.toFixed(1)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all duration-500 ${
                budget.percentUsed >= 90
                  ? 'bg-red-500'
                  : budget.percentUsed >= 70
                  ? 'bg-yellow-500'
                  : 'bg-green-500'
              }`}
              style={{ width: `${Math.min(budget.percentUsed, 100)}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            {formatCurrency(budget.remaining)} remaining today
          </p>
        </div>
      </div>

      {/* Daily Cost Chart (last 30 days) */}
      {dailyCosts.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Daily Cost Trend (30 Days)
          </h3>
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
            <div className="flex items-end gap-1 h-32">
              {dailyCosts.map((day) => {
                const height = maxDailyCost > 0
                  ? Math.max((day.totalCost / maxDailyCost) * 100, 2)
                  : 2;
                return (
                  <div
                    key={day.date}
                    className="flex-1 group relative"
                    title={`${day.date}: ${formatCurrency(day.totalCost)} (${day.reviewCount} reviews)`}
                  >
                    <div
                      className="w-full bg-blue-400 dark:bg-blue-500 rounded-t hover:bg-blue-500 dark:hover:bg-blue-400 transition-colors cursor-help"
                      style={{ height: `${height}%` }}
                    />
                    {/* Tooltip on hover */}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10 pointer-events-none">
                      <div className="bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded px-2 py-1 whitespace-nowrap shadow-lg">
                        <div>{new Date(day.date).toLocaleDateString()}</div>
                        <div>{formatCurrency(day.totalCost)}</div>
                        <div>{day.reviewCount} reviews</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between mt-2 text-xs text-gray-400 dark:text-gray-500">
              <span>
                {dailyCosts.length > 0
                  ? new Date(dailyCosts[0].date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                  : ''}
              </span>
              <span>
                {dailyCosts.length > 0
                  ? new Date(dailyCosts[dailyCosts.length - 1].date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                  : ''}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Cost by Model (30 days) */}
      {byModel.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            Cost by Model (30 Days)
          </h3>
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800">
                  <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Model</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Tier</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Reviews</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Total Cost</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Avg Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {byModel.map((row, i) => (
                  <tr key={i} className="bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <td className="px-4 py-2 text-gray-900 dark:text-gray-100 font-mono text-xs truncate max-w-[200px]" title={row.model || 'Unknown'}>
                      {row.model || 'Unknown'}
                    </td>
                    <td className="px-4 py-2">
                      {row.tier && (
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${tierColors[row.tier] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'}`}>
                          {row.tier}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">{row.reviewCount}</td>
                    <td className="px-4 py-2 text-right text-gray-900 dark:text-gray-100 font-medium">{formatCurrency(row.totalCost)}</td>
                    <td className="px-4 py-2 text-right text-gray-500 dark:text-gray-400">{formatCurrency(row.avgCost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Cost by Tier (30 days) */}
      {byTier.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            Cost by Tier (30 Days)
          </h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {byTier.map((row) => (
              <div
                key={row.tier || 'unknown'}
                className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${tierColors[row.tier || ''] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'}`}>
                    {row.tier || 'unknown'}
                  </span>
                  <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                    {formatCurrency(row.totalCost)}
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {row.reviewCount} review{row.reviewCount !== 1 ? 's' : ''}
                  {row.reviewCount > 0 && ` -- ${formatCurrency(row.totalCost / row.reviewCount)} avg`}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Token Usage Summary */}
      {(costSummary.avgInputTokens > 0 || costSummary.avgOutputTokens > 0) && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Today's Token Usage
          </h3>
          <div className="flex gap-6 text-sm">
            <div>
              <span className="text-gray-500 dark:text-gray-400">Input: </span>
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {formatNumber(costSummary.avgInputTokens)}
              </span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Output: </span>
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {formatNumber(costSummary.avgOutputTokens)}
              </span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Total: </span>
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {formatNumber(costSummary.avgInputTokens + costSummary.avgOutputTokens)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

AICostDashboard.displayName = 'AICostDashboard';

export default AICostDashboard;
